import React from 'react';
import usStatesAndProvinces from './usStatesAndProvinces';
import countries from './countries';
import EditableTextField from './EditableTextField';
import EditableSelectField from './EditableSelectField';
import EditableMultiSelectField from './EditableMultiSelectField';

export default function ProjectInformationTab({ project, editable, handleInputChange, handleSelectChange }) {
  return (
    <table className='project-detail-table'>
      <tbody>
        <tr>
          {/* picklist */}
          <td>Program Version</td>
          <td>
            <EditableSelectField
              name='program_version'
              value={project.program_version}
              onChange={handleInputChange}
              editable={editable === true}
              disabledFallback='text'
              options={[
                'PHIUS+ 2012',
                'PHIUS+ 2015',
                'PHIUS+ 2015 Source Zero',
                'PHIUS+ 2018 CORE',
                'PHIUS+ 2018',
                'PHIUS+ 2018 Source Zero',
                'Phius CORE 2021',
                'Phius ZERO 2021',
                'Phius CORE REVIVE 2021',
                'Phius ZERO REVIVE 2021',
                'Phius CORE Prescriptive 2021',
              ]}
            />
          </td>
        </tr>
        <tr>
          <td>Construction Type</td>
          <td>
            <EditableMultiSelectField
              name='construction_type'
              value={project.construction_type}
              onChange={(option) => handleSelectChange('construction_type', option)}
              editable={editable === true}
              options={[
                { value: 'Wood-Frame', label: 'Wood-Frame' },
                { label: 'Heavy Timber', value: 'Heavy Timber' },
                { label: 'Masonry', value: 'Masonry' },
                {
                  label: 'Insulated Concrete Form',
                  value: 'Insulated Concrete Form',
                },
                {
                  label: 'Autoclaved aerated concrete (AAC)',
                  value: 'Autoclaved aerated concrete (AAC)',
                },
                { label: 'Steel', value: 'Steel' },
              ]}
            />
          </td>
        </tr>
        <tr>
          <td>Facade Type</td>
          <td>
            <EditableSelectField
              name='facade_type'
              value={project.facade_type}
              onChange={handleInputChange}
              editable={editable === true}
              options={['Masonry Veneer', 'Fiber Cement', 'Metal Panels', 'EIFS']}
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>Number of Units</td>
          <td>
            <EditableTextField
              name='number_of_units'
              value={project.number_of_units}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>Number of Stories</td>
          <td>
            <EditableTextField
              name='number_of_stories'
              value={project.number_of_stories}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>Interior Conditioned Floor Area</td>
          <td>
            <EditableTextField
              name='interior_conditioned_floor_area'
              value={project.interior_conditioned_floor_area}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
              unit='ft²'
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>Gross Floor Area</td>
          <td>
            <EditableTextField
              name='gross_floor_area'
              value={project.gross_floor_area}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
              unit='ft²'
            />
          </td>
        </tr>
        <tr>
          <td>Street Address</td>
          <td>
            <EditableTextField
              name='street_address'
              value={project.street_address}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          <td>City</td>
          <td>
            <EditableTextField
              name='city'
              value={project.city}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        {/* State/Province: not folded — the editable branch uses a CONTROLLED
            `value={project.state}` select (every other select in the file uses
            uncontrolled `defaultValue`); EditableSelectField always renders
            `defaultValue`, so converting would change this from a controlled to an
            uncontrolled component — a real (if subtle) behavior difference, not
            silently made here. */}
        <tr>
          <td>State/Province</td>
          <td>
            {editable === true ? (
              <select
                name='state'
                onChange={(e) => handleInputChange(e)}
                className='edit-input'
                value={project.state}
              >
                <option value=''></option>
                {usStatesAndProvinces.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                name='state'
                onChange={(e) => handleInputChange(e)}
                className='project-detail-input'
                disabled
                value={project.state}
              />
            )}
          </td>
        </tr>
        <tr>
          <td>Zip Code</td>
          <td>
            <EditableTextField
              name='zip_code'
              value={project.zip_code}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          {/* picklist */}
          <td>Country</td>
          <td>
            <EditableSelectField
              name='country'
              value={project.country}
              onChange={handleInputChange}
              editable={editable === true}
              disabledFallback='text'
              emptyOption={false}
              options={countries}
            />
          </td>
        </tr>
        {/* ASHRAE Climate Zone: not folded — the editable branch has `deafultValue`
            (typo for `defaultValue`), a pre-existing bug that makes this dropdown
            never show the saved value in edit mode. Converting to EditableSelectField
            (which always uses a correctly-spelled defaultValue) would silently fix the
            typo as a side effect of the mechanical rollout. Preserve as-is pending a
            decision — see
            DEV_NOTES/FOUND_WORK/ashrae-climate-zone-defaultvalue-typo.md. */}
        <tr>
          {/* picklist */}
          <td>ASHRAE Climate Zone</td>
          <td>
            {editable === true ? (
              <select
                name='ASHRAE_climate_zone'
                onChange={(e) => handleInputChange(e)}
                className='edit-input'
                deafultValue={project.ASHRAE_climate_zone}
              >
                <option value=''></option>
                <option value='0A - Extremely Hot - Humid'>
                  0A - Extremely Hot - Humid
                </option>
                <option value='0B - Extremely Hot - Dry'>
                  0B - Extremely Hot - Dry
                </option>
                <option value='1A - Very Hot - Humid'>
                  1A - Very Hot - Humid
                </option>
                <option value='1B - Very Hot - Humid'>
                  1B - Very Hot - Humid
                </option>
                <option value='2A - Hot - Humid'>2A - Hot - Humid</option>
                <option value='2B - Hot - Dry'>2B - Hot - Dry</option>
                <option value='3A - Warm - Humid'>
                  3A - Warm - Humid
                </option>
                <option value='3B - Warm - Dry'>3B - Warm - Dry</option>
                <option value='4A - Mixed - Humid'>
                  4A - Mixed - Humid
                </option>
                <option value='4B - Mixed - Dry'>4B - Mixed - Dry</option>
                <option value='4C - Mixed - Marine'>
                  4C - Mixed - Marine
                </option>
                <option value='5A - Cool - Humid'>
                  5A - Cool - Humid
                </option>
                <option value='5B - Cool - Dry'>5B - Cool - Dry</option>
                <option value='5C - Cool - Marine'>
                  5C - Cool - Marine
                </option>
                <option value='6A - Cool - Humid'>
                  6A - Cool - Humid
                </option>
                <option value='6B - Cool -  Dry'>6B - Cool - Dry</option>
                <option value='7 - Very Cold'>7 - Very Cold</option>
                <option value='8 - Subarctic / Arctic'>
                  8 - Subarctic / Arctic
                </option>
              </select>
            ) : (
              project.ASHRAE_climate_zone
            )}
          </td>
        </tr>
        <tr>
          {/* picklist */}
          <td>Compliance Software</td>
          <td>
            <EditableSelectField
              name='compliance_software'
              value={project.compliance_software}
              onChange={handleInputChange}
              editable={editable === true}
              disabledFallback='text'
              options={['WUFI Passive', 'PHPP', 'N/A']}
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>Energy Star Rating</td>
          <td>
            <EditableTextField
              name='energy_star_rating'
              value={project.energy_star_rating}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>HERS Rating</td>
          <td>
            <EditableTextField
              name='HERS_rating'
              value={project.HERS_rating}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
            />
          </td>
        </tr>
        <tr>
          {/* number */}
          <td>HERS Rating with Renewables</td>
          <td>
            <EditableTextField
              name='HERS_rating_with_renewables'
              value={project.HERS_rating_with_renewables}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
            />
          </td>
        </tr>
        <tr>
          <td>Window-to-Wall Ratio</td>
          <td>
            <EditableTextField
              name='window_to_wall_ratio'
              value={project.window_to_wall_ratio}
              onChange={handleInputChange}
              editable={editable === true}
              type='number'
              unit='%'
            />
          </td>
        </tr>
        {/* multiselect */}
        {project.additional_certifications !== undefined &&
          project.additional_certifications.length > 0 ? (
          <tr>
            <td>Additional Certification</td>
            <td>
              <EditableMultiSelectField
                name='additional_certifications'
                value={project.additional_certifications}
                onChange={(option) => handleSelectChange('additional_certifications', option)}
                editable={editable === true}
                options={[
                  { value: 'LEED Platinum', label: 'LEED Platinum' },
                  { label: 'LEED Gold', value: 'LEED Gold' },
                  { label: 'LEED Silver', value: 'LEED Silver' },
                  { label: 'LEED Certified', value: 'LEED Certified' },
                  {
                    label: 'Enterprise Green Communities',
                    value: 'Enterprise Green Communities',
                  },
                  {
                    label: 'Enterprise Green Communities Plus',
                    value: 'Enterprise Green Communities Plus',
                  },
                  { label: 'ILFI Petal', value: 'ILFI Petal' },
                  {
                    label: 'ILFI Zero Energy',
                    value: 'ILFI Zero Energy',
                  },
                  {
                    label: 'ILFI Zero Carbon+',
                    value: 'ILFI Zero Carbon+',
                  },
                  { label: 'NGBS', value: 'NGBS' },
                  { label: 'NGBS Emerald', value: 'NGBS Emerald' },
                ]}
              />
            </td>
          </tr>
        ) : (
          ''
        )}
        {/* Outer guard as-written in source: `.length !== undefined` doesn't actually
            protect against `additional_certification_badges` itself being null/
            undefined (unlike the `!== undefined` check above) — if it ever were, this
            line would throw. Left exactly as-is (not touched by this refactor); noted
            in DEV_NOTES since it's a latent crash risk, not something to silently
            "fix" as part of the mechanical field-component rollout. */}
        {project.additional_certification_badges.length !== undefined &&
          project.additional_certification_badges.length > 0 ? (
          <tr>
            <td>Additional Certification Badges</td>
            <td>
              <EditableMultiSelectField
                name='additional_certification_badges'
                value={project.additional_certification_badges}
                onChange={(option) =>
                  handleSelectChange('additional_certification_badges', option)
                }
                editable={editable === true}
                options={[
                  {
                    value: 'Supply Air Heating and Cooling Sufficient',
                    label: 'Supply Air Heating and Cooling Sufficient',
                  },
                  {
                    label: 'PHIUS+ Source Zero',
                    value: 'PHIUS+ Source Zero',
                  },
                ]}
              />
            </td>
          </tr>
        ) : (
          ''
        )}
      </tbody>
    </table>
  );
}
